import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { SolMaterialEscalationDialogDataModel } from '../../../../models/sol-estimate-table/SolMaterialEscalationDialogDataModel';
import { FormGroup, FormsModule, ReactiveFormsModule, FormControl, Validators } from "@angular/forms";
import { EscalationPeriod } from '../../../../models/enum';
import { CONSTANTS } from '../../../../Constants/Constants';
import { EscalationDataTable } from '../../../../models/sol-estimate-table/EscalationDataTable';
import { CommonDataService } from '../../../../services/common-data.service';
import { EstimateCommonFunctionsService } from '../../../../services/estimate/estimate-common-functions.service';
import { roundHalfEven9 } from '../../../../../types/number-extensions';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { DecimalPrecisionPipe } from '../../../../pipes/decimal-precision-pipe/decimal-precision.pipe';
import { NumericMaskDirective } from '../../../../shared/numeric/numeric-mask.directive';
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';

type EscalationControlName = 'firstYearEscalation' | 'secondYearEscalation' | 'thirdYearEscalation' | 'fourthYearEscalation' | 'fifthYearEscalation';
type EscalationPercentageKey = 'firstYearPercentage' | 'secondYearPercentage' | 'thirdYearPercentage' | 'fourthYearPercentage' | 'fifthYearPercentage';

@Component({
  selector: 'app-sol-estimate-escalation-dialog',
  imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    TranslatePipe,
    MatButtonModule,
    MatTableModule,
    MatFormField,
    MatInput,
    CommonModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    MultilingualCurrencyPipe,
    NumericMaskDirective,
    CloseOnEscapeDirective
],
  templateUrl: './sol-estimate-escalation-dialog.component.html',
  styleUrl: './sol-estimate-escalation-dialog.component.scss'
})
export class SolEstimateEscalationDialogComponent implements OnInit, OnDestroy {

  /** The only editable control on this dialog — the per-year escalation percentage. */
  readonly percentageConfig: NumericFieldConfig = {
    kind: 'percentage',
    scale: CONSTANTS.ZERO,
    step: CONSTANTS.ONE,
    allowNegative: false,
    min: CONSTANTS.ZERO,
    max: CONSTANTS.HUNDRED,
    useGrouping: false,
  };


  escalationRatesFormGroup: FormGroup<{
  firstYearEscalation: FormControl<number>;
  secondYearEscalation: FormControl<number>;
  thirdYearEscalation: FormControl<number>;
  fourthYearEscalation: FormControl<number>;
  fifthYearEscalation: FormControl<number>;
}> = new FormGroup(
    {
      firstYearEscalation: new FormControl(100, [Validators.required, Validators.min(0), Validators.max(100)]),
      secondYearEscalation: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      thirdYearEscalation: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      fourthYearEscalation: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      fifthYearEscalation: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)])
    }
  );
  escalationDataSource: EscalationDataTable[] = []
  displayedColumns: string[] = ['escalationPeriodName', 'FIRST_YEAR', 'SECOND_YEAR', 'THIRD_YEAR', 'FOURTH_YEAR', 'FIFTH_YEAR'];
  escalationPeriods: string[] = [
    EscalationPeriod.FIRST_YEAR_PERCENTAGE,
    EscalationPeriod.SECOND_YEAR_PERCENTAGE,
    EscalationPeriod.THIRD_YEAR_PERCENTAGE,
    EscalationPeriod.FOURTH_YEAR_PERCENTAGE,
    EscalationPeriod.FIFTH_YEAR_PERCENTAGE
  ];
  public isValidEscalation: boolean = true;
  public escalationEnabled: boolean = true;
  public applyToAll: boolean = false;
  public formattedStartDate: string = '';
  public formattedEndDate: string = '';
  public totalEscalatedCost: number = 0;
  private numericEscalatedCosts: number[] = [0, 0, 0, 0, 0];
  isCollaborativeNotEdit: boolean = false;
  isEstimateSapSubmitted: boolean = false;
  /** First year column that may hold a distribution — earlier columns are before the job start financial year. */
  public firstApplicableYearIndex: number = 0;
  /** Job starts after the fifth escalation period ends, so no cost can be escalated. */
  public isOutOfEscalationRange: boolean = false;
  /** Year columns that sit before the job start financial year, indexed by year slot. */
  public disabledYears: boolean[] = [false, false, false, false, false];
  private readonly yearControlNames: readonly EscalationControlName[] = [
    'firstYearEscalation', 'secondYearEscalation', 'thirdYearEscalation', 'fourthYearEscalation', 'fifthYearEscalation'
  ];
  private readonly yearPercentageKeys: readonly EscalationPercentageKey[] = [
    'firstYearPercentage', 'secondYearPercentage', 'thirdYearPercentage', 'fourthYearPercentage', 'fifthYearPercentage'
  ];
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public escalationDialogData: SolMaterialEscalationDialogDataModel,
    private readonly dialogRef: MatDialogRef<SolEstimateEscalationDialogComponent>,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly commonDataService: CommonDataService,
    private readonly translateService: TranslateService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly decimalPrecisionPipe : DecimalPrecisionPipe
  ) {}
  
  ngOnInit(): void {
    this.isEstimateSapSubmitted = this.escalationDialogData.isEscalationDisabled ?? false;
    this.initializeProperties();
    this.mapValuesToFormGroup();
    this.setDialogData();
    this.getEscalationForAllPeriods();
    this.updateTotalEscalatedCost();
    this.isCollaborativeNotEdit = this.estimateCommonFunctions.updateCollaborativeState();
    this.getFormControlState();
    this.isEscalationChanged();
  }

  initializeProperties(): void {
    // Default to true, but will be updated based on the first year percentage value in the form group.
    this.escalationEnabled = true;

    if (this.escalationDialogData.escalationType === CONSTANTS.MATERIAL) {
      this.applyToAll = this.escalationDialogData.applyMaterialEscalationToAll ?? false;
    } else {
      this.applyToAll = this.escalationDialogData.applyLaborEscalationToAll ?? false;
    }

    const estimateData = this.commonDataService.getEstimateData();
    const startDate = estimateData?.startDate ? new Date(estimateData.startDate) : new Date(this.escalationDialogData.escalationData.escalationStartDate);
    this.formattedStartDate = this.formatDate(startDate);

    const endDate = estimateData?.endDate ? new Date(estimateData.endDate) : new Date(startDate);
    if (!estimateData?.endDate) {
      endDate.setFullYear(endDate.getFullYear() + 5);
    }
    this.formattedEndDate = this.formatDate(endDate);

    this.firstApplicableYearIndex = this.getFirstApplicableYearIndex(startDate);
    this.isOutOfEscalationRange = this.getIsOutOfEscalationRange(startDate);
    this.disabledYears = this.yearControlNames.map((_, index) => this.isOutOfEscalationRange || index < this.firstApplicableYearIndex);

    if (this.isOutOfEscalationRange) {
      this.escalationEnabled = false;
      this.yearPercentageKeys.forEach(key => this.escalationDialogData.escalationData[key] = CONSTANTS.ZERO);
      return;
    }

    this.setDefaultEscalationPercentages();
  }

  /** Beyond the fifth period there is no column left to carry the cost, so escalation is off. */
  private getIsOutOfEscalationRange(jobStartDate: Date): boolean {
    const anchorFiscalYear = this.getFiscalYear(new Date(this.escalationDialogData.escalationData.escalationStartDate));
    const offset = this.getFiscalYear(jobStartDate) - anchorFiscalYear;
    return Number.isFinite(offset) && offset >= this.yearControlNames.length;
  }

  /**
   * Year columns are anchored to the escalation start date's financial year, so a job that starts
   * in a later financial year cannot distribute cost into the columns before it.
   */
  private getFirstApplicableYearIndex(jobStartDate: Date): number {
    const anchorFiscalYear = this.getFiscalYear(new Date(this.escalationDialogData.escalationData.escalationStartDate));
    const startFiscalYear = this.getFiscalYear(jobStartDate);
    const offset = startFiscalYear - anchorFiscalYear;
    if (!Number.isFinite(offset)) {
      return CONSTANTS.ZERO;
    }
    return Math.min(Math.max(offset, CONSTANTS.ZERO), this.yearControlNames.length - 1);
  }

  /** The financial year is named after the calendar year in which its Oct 1 falls. */
  private getFiscalYear(date: Date): number {
    return date.getMonth() >= CONSTANTS.FISCAL_YEAR_START_MONTH ? date.getFullYear() : date.getFullYear() - 1;
  }

  private formatDate(date: Date): string {
    const locale = this.translateService.currentLang || this.translateService.defaultLang || CONSTANTS.LANGUAGE_EN_US;
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  }


  /**
   * When all year percentages are 0 (no prior distribution), default the first year to 100%
   * so that the full base cost is allocated to Period 1 and the escalated cost reflects the actual cost.
   */
  private setDefaultEscalationPercentages(): void {
    const escalationData = this.escalationDialogData.escalationData;
    const totalPercentage =
      (escalationData.firstYearPercentage || 0) +
      (escalationData.secondYearPercentage || 0) +
      (escalationData.thirdYearPercentage || 0) +
      (escalationData.fourthYearPercentage || 0) +
      (escalationData.fifthYearPercentage || 0);

    if (totalPercentage === 0) {
      escalationData[this.yearPercentageKeys[this.firstApplicableYearIndex]] = CONSTANTS.HUNDRED_POINT_ZERO;
    }
  }

  mapValuesToFormGroup() {
    this.escalationRatesFormGroup.controls['firstYearEscalation'].setValue(this.escalationDialogData.escalationData.firstYearPercentage);
    this.escalationRatesFormGroup.controls['secondYearEscalation'].setValue(this.escalationDialogData.escalationData.secondYearPercentage);
    this.escalationRatesFormGroup.controls['thirdYearEscalation'].setValue(this.escalationDialogData.escalationData.thirdYearPercentage);
    this.escalationRatesFormGroup.controls['fourthYearEscalation'].setValue(this.escalationDialogData.escalationData.fourthYearPercentage);
    this.escalationRatesFormGroup.controls['fifthYearEscalation'].setValue(this.escalationDialogData.escalationData.fifthYearPercentage);
  }

  setDialogData() {
    const periodStartLabels = this.buildPeriodStartLabels();
    this.escalationDataSource = [
      {
      escalationPeriodName: '',
      FIRST_YEAR: periodStartLabels[0],
      SECOND_YEAR: periodStartLabels[1],
      THIRD_YEAR: periodStartLabels[2],
      FOURTH_YEAR: periodStartLabels[3],
      FIFTH_YEAR: periodStartLabels[4]
      },
      {
      escalationPeriodName: 'PERCENTAGE',
      FIRST_YEAR: `${this.escalationDialogData.escalationData.firstYearPercentage}${CONSTANTS.PERCENT}`,
      SECOND_YEAR: `${this.escalationDialogData.escalationData.secondYearPercentage}${CONSTANTS.PERCENT}`,
      THIRD_YEAR: `${this.escalationDialogData.escalationData.thirdYearPercentage}${CONSTANTS.PERCENT}`,
      FOURTH_YEAR: `${this.escalationDialogData.escalationData.fourthYearPercentage}${CONSTANTS.PERCENT}`,
      FIFTH_YEAR: `${this.escalationDialogData.escalationData.fifthYearPercentage}${CONSTANTS.PERCENT}`
      },
      {
      escalationPeriodName: 'BASE_COST',
      FIRST_YEAR: this.formatCurrency(this.getDistributedBaseCost(this.escalationDialogData.escalationData.firstYearPercentage)),
      SECOND_YEAR: this.formatCurrency(this.getDistributedBaseCost(this.escalationDialogData.escalationData.secondYearPercentage)),
      THIRD_YEAR: this.formatCurrency(this.getDistributedBaseCost(this.escalationDialogData.escalationData.thirdYearPercentage)),
      FOURTH_YEAR: this.formatCurrency(this.getDistributedBaseCost(this.escalationDialogData.escalationData.fourthYearPercentage)),
      FIFTH_YEAR: this.formatCurrency(this.getDistributedBaseCost(this.escalationDialogData.escalationData.fifthYearPercentage))
      },
      {
      escalationPeriodName: 'ESCALATION_RATE',
      FIRST_YEAR: `${CONSTANTS.ZERO_POINT_ZERO}${CONSTANTS.PERCENT}`,
      SECOND_YEAR: `${this.formatDecimal(roundHalfEven9(this.escalationDialogData.escalationData.secondYearRate))}${CONSTANTS.PERCENT}`,
      THIRD_YEAR: `${this.formatDecimal(roundHalfEven9(this.escalationDialogData.escalationData.thirdYearRate))}${CONSTANTS.PERCENT}`,
      FOURTH_YEAR: `${this.formatDecimal(roundHalfEven9(this.escalationDialogData.escalationData.fourthYearRate))}${CONSTANTS.PERCENT}`,
      FIFTH_YEAR: `${this.formatDecimal(roundHalfEven9(this.escalationDialogData.escalationData.fifthYearRate))}${CONSTANTS.PERCENT}`,
      },
      {
      escalationPeriodName: 'ESCALATED_COST',
      FIRST_YEAR: this.formatCurrency(0),
      SECOND_YEAR: this.formatCurrency(0),
      THIRD_YEAR: this.formatCurrency(0),
      FOURTH_YEAR: this.formatCurrency(0),
      FIFTH_YEAR: this.formatCurrency(0)
      }
    ];
  }

  /**
   * Each column covers one financial year, so it is labelled with that year's Oct 1 start —
   * anchored on the financial year the escalation start date falls in, not on the date itself.
   */
  private buildPeriodStartLabels(): string[] {
    const anchorFiscalYear = new Date(this.escalationDialogData.escalationData.escalationStartDate);
    const locale = this.translateService.currentLang || this.translateService.defaultLang || CONSTANTS.LANGUAGE_EN_US;

    return this.yearControlNames.map((_, offset) => {
      const periodStart = new Date(anchorFiscalYear.getFullYear() + offset, CONSTANTS.FISCAL_YEAR_START_MONTH, 1);
      const month = periodStart.toLocaleString(locale, { month: 'short' });
      const day = periodStart.toLocaleString(locale, { day: '2-digit' });
      return `${month} ${day}, ${periodStart.getFullYear()}`;
    });
  }

  getEscalationForAllPeriods(): void {
    this.escalationPeriods.forEach(period => {
      this.getEscalatedCost(period);
    });
  }

  getEscalatedCost(escalationPeriod: string) {
    let baseCost = this.escalationDialogData.escalationData.baseCost;
    const index = this.escalationDataSource.findIndex(data => data.escalationPeriodName === 'ESCALATED_COST');
    const rates = [
      CONSTANTS.ZERO_POINT_ZERO,
      this.escalationDialogData.escalationData.secondYearRate,
      this.escalationDialogData.escalationData.thirdYearRate,
      this.escalationDialogData.escalationData.fourthYearRate,
      this.escalationDialogData.escalationData.fifthYearRate
    ];
    const yearKeys: Array<{ period: string; controlName: EscalationControlName; yearIndex: number; field: keyof EscalationDataTable }> = [
      { period: EscalationPeriod.FIRST_YEAR_PERCENTAGE, controlName: 'firstYearEscalation', yearIndex: 0, field: 'FIRST_YEAR' },
      { period: EscalationPeriod.SECOND_YEAR_PERCENTAGE, controlName: 'secondYearEscalation', yearIndex: 1, field: 'SECOND_YEAR' },
      { period: EscalationPeriod.THIRD_YEAR_PERCENTAGE, controlName: 'thirdYearEscalation', yearIndex: 2, field: 'THIRD_YEAR' },
      { period: EscalationPeriod.FOURTH_YEAR_PERCENTAGE, controlName: 'fourthYearEscalation', yearIndex: 3, field: 'FOURTH_YEAR' },
      { period: EscalationPeriod.FIFTH_YEAR_PERCENTAGE, controlName: 'fifthYearEscalation', yearIndex: 4, field: 'FIFTH_YEAR' },
    ];
    const entry = yearKeys.find(k => k.period === escalationPeriod);
    if (entry) {
      const escalatedCost = this.estimateCommonFunctions.calculateEscalatedCostForPeriod(
        baseCost, this.escalationRatesFormGroup.controls[entry.controlName].value, rates, entry.yearIndex
      );
      this.escalationDataSource[index][entry.field] = this.formatCurrency(roundHalfEven9(escalatedCost));
      this.numericEscalatedCosts[entry.yearIndex] = escalatedCost;
    }
    this.escalationDataSource = [...this.escalationDataSource];
  }

  escalationPercentChange(escalationYear: string) {
    if (this.isCollaborativeNotEdit) {
      return;
    }
    this.sumToHundredValidator();
    this.updateEscalationEnabled();
    this.updateBaseCostDistribution();
    this.getEscalatedCost(escalationYear);
    this.updateTotalEscalatedCost();
  }

  isIndividualEscalationApplicable(event: MatCheckboxChange): void {
    if (event.checked) {
      this.escalationEnabled = true;
      this.sumToHundredValidator();
      this.updateBaseCostDistribution();
      this.getEscalationForAllPeriods();
      this.updateTotalEscalatedCost();
    } else {
      this.escalationEnabled = false;
      this.resetEscalationDistribution();
      this.updateTotalEscalatedCost();
    }
  }

  private resetEscalationDistribution(): void {
    this.yearControlNames.forEach((controlName, index) => {
      const value = index === this.firstApplicableYearIndex ? CONSTANTS.HUNDRED_POINT_ZERO : CONSTANTS.ZERO;
      this.escalationRatesFormGroup.controls[controlName].setValue(value);
    });
    this.isValidEscalation = true;
    this.updateBaseCostDistribution();
    this.getEscalationForAllPeriods();
  }

  /**
   * Auto-toggles the escalationEnabled checkbox based on period distribution.
   * If Period 1 is 100% (no cost spread across years), escalation is disabled.
   * If cost is distributed to other periods, escalation is enabled.
   */
  private updateEscalationEnabled(): void {
    const firstApplicableControl = this.yearControlNames[this.firstApplicableYearIndex];
    const firstApplicableValue = Number(this.escalationRatesFormGroup.controls[firstApplicableControl].value || 0);
    this.escalationEnabled = firstApplicableValue !== CONSTANTS.HUNDRED_POINT_ZERO;
  }

  private isEscalationChanged() : void{
    this.yearControlNames.forEach((controlName,index) => {
      this.escalationRatesFormGroup.controls[controlName].valueChanges.pipe(debounceTime(300),takeUntil(this.destroy$)).subscribe((value) => {
        if (value == null) {
          this.escalationRatesFormGroup.controls[controlName].setValue(CONSTANTS.ZERO, { emitEvent: false });
        }
        this.escalationPercentChange(this.escalationPeriods[index]);
      });
    });
  }

  sumToHundredValidator() {
    if (this.escalationRatesFormGroup) {
      const total =
        Number(this.escalationRatesFormGroup.controls['firstYearEscalation'].value || 0) +
        Number(this.escalationRatesFormGroup.controls['secondYearEscalation'].value || 0) +
        Number(this.escalationRatesFormGroup.controls['thirdYearEscalation'].value || 0) +
        Number(this.escalationRatesFormGroup.controls['fourthYearEscalation'].value || 0) +
        Number(this.escalationRatesFormGroup.controls['fifthYearEscalation'].value || 0);
       this.isValidEscalation = (total === CONSTANTS.HUNDRED_POINT_ZERO);
    }
  }

  // ── Escalation Calculations ────────────────────────────────────

  getDistributedBaseCost(yearPercentage: number): number {
    return roundHalfEven9((this.escalationDialogData.escalationData.baseCost * yearPercentage) / CONSTANTS.HUNDRED_POINT_ZERO);
  }

  updateBaseCostDistribution(): void {
    const baseCostIndex = this.escalationDataSource.findIndex(d => d.escalationPeriodName === 'BASE_COST');
    if (baseCostIndex >= 0) {
      const controls = this.escalationRatesFormGroup.controls;
      this.escalationDataSource[baseCostIndex].FIRST_YEAR = this.formatCurrency(this.getDistributedBaseCost(controls['firstYearEscalation'].value));
      this.escalationDataSource[baseCostIndex].SECOND_YEAR = this.formatCurrency(this.getDistributedBaseCost(controls['secondYearEscalation'].value));
      this.escalationDataSource[baseCostIndex].THIRD_YEAR = this.formatCurrency(this.getDistributedBaseCost(controls['thirdYearEscalation'].value));
      this.escalationDataSource[baseCostIndex].FOURTH_YEAR = this.formatCurrency(this.getDistributedBaseCost(controls['fourthYearEscalation'].value));
      this.escalationDataSource[baseCostIndex].FIFTH_YEAR = this.formatCurrency(this.getDistributedBaseCost(controls['fifthYearEscalation'].value));
      this.escalationDataSource = [...this.escalationDataSource];
    }
  }

  updateTotalEscalatedCost(): void {
    if (this.escalationEnabled) {
      this.totalEscalatedCost = this.numericEscalatedCosts.reduce((sum, cost) => sum + cost, CONSTANTS.ZERO);
    } else {
      this.totalEscalatedCost = this.escalationDialogData.escalationData.baseCost;
    }
  }

  getFormControlState() {
    this.yearControlNames.forEach((controlName, index) => {
      const control = this.escalationRatesFormGroup.controls[controlName];
      if (this.isCollaborativeNotEdit || this.disabledYears[index]) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }
  onOk(): void {
    this.escalationDialogData.escalationData.firstYearPercentage = this.escalationRatesFormGroup.controls['firstYearEscalation'].value;
    this.escalationDialogData.escalationData.secondYearPercentage = this.escalationRatesFormGroup.controls['secondYearEscalation'].value;
    this.escalationDialogData.escalationData.thirdYearPercentage = this.escalationRatesFormGroup.controls['thirdYearEscalation'].value;
    this.escalationDialogData.escalationData.fourthYearPercentage = this.escalationRatesFormGroup.controls['fourthYearEscalation'].value;
    this.escalationDialogData.escalationData.fifthYearPercentage = this.escalationRatesFormGroup.controls['fifthYearEscalation'].value;
    this.escalationDialogData.escalationData.escalatedCost = this.totalEscalatedCost;
    this.escalationDialogData.escalationData.escalationEnabled = this.escalationEnabled;

    if (this.escalationDialogData.escalationType === CONSTANTS.MATERIAL) {
      this.escalationDialogData.applyMaterialEscalationToAll = this.applyToAll;
    } else {
      this.escalationDialogData.applyLaborEscalationToAll = this.applyToAll;
    }

    this.dialogRef.close(this.escalationDialogData);
  }
  
  private formatCurrency(value: number): string {
    return this.multilingualCurrencyPipe.transform(value);
  }
  private formatDecimal(value: number): string {
    return this.decimalPrecisionPipe.transform(value);
  }

  ngOnDestroy(): void {
    this.isCollaborativeNotEdit = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

}
